import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { getUuid, isAbsoluteEmpty, typedOmit } from 'ziphy-web-shared/basic/helpers';
import { useInnerValue } from 'ziphy-web-shared/basic/hooks';
export function useFieldWrapper(props, forwardedRef) {
    const originalFieldRef = useRef(null);
    if (!props.headerActions) {
        props.headerActions = [];
    }
    const [isFocused, setIsFocused] = useState(false);
    const isUnfocused = !isFocused;
    const { value, onChange, isInvalid } = props.field || {};
    const [innerValue, setInnerValue] = useInnerValue(value);
    const id = useMemo(() => (props.id ? props.id : getUuid()), [props.id]);
    const size = useMemo(() => {
        for (const x of Array.isArray(props.mode) ? props.mode : [props.mode]) {
            const sizes = ['xxs', 'xs', 'sm', 'md', 'lg'];
            if (sizes.indexOf(x) !== -1)
                return x;
        }
    }, [props.mode]);
    useEffect(() => {
        if (props.disabled) {
            setIsFocused(false);
        }
    }, [props.disabled]);
    const handleOnChange = useCallback((val) => {
        setInnerValue(val);
        if (typeof onChange === 'function') {
            onChange(val);
        }
    }, [onChange]);
    const handleOnClick = useCallback((e) => {
        var _a, _b, _c;
        if (props.readonly || props.disabled)
            return;
        (_a = e === null || e === void 0 ? void 0 : e.preventDefault) === null || _a === void 0 ? void 0 : _a.call(e);
        (_b = e === null || e === void 0 ? void 0 : e.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(e);
        if (!isFocused) {
            (_c = originalFieldRef.current) === null || _c === void 0 ? void 0 : _c.focus();
        }
        if (typeof props.onClick === 'function') {
            props.onClick(e);
        }
    }, [props.readonly, props.disabled, props.onClick, isFocused]);
    const handleOnFocus = useCallback((e) => {
        var _a, _b;
        if (props.readonly || props.disabled)
            return;
        (_a = e === null || e === void 0 ? void 0 : e.preventDefault) === null || _a === void 0 ? void 0 : _a.call(e);
        (_b = e === null || e === void 0 ? void 0 : e.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(e);
        setIsFocused(true);
        if (typeof props.onFocus === 'function') {
            props.onFocus(e);
        }
    }, [props.readonly, props.disabled, props.onFocus]);
    const handleOnBlur = useCallback((e) => {
        var _a, _b;
        if (props.readonly || props.disabled)
            return;
        (_a = e === null || e === void 0 ? void 0 : e.preventDefault) === null || _a === void 0 ? void 0 : _a.call(e);
        (_b = e === null || e === void 0 ? void 0 : e.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(e);
        setIsFocused(false);
        if (typeof props.onBlur === 'function') {
            props.onBlur(e);
        }
    }, [props.readonly, props.disabled, props.onBlur]);
    const handleOnClear = useCallback((e) => {
        var _a, _b, _c;
        if (props.readonly || props.disabled)
            return;
        (_a = e === null || e === void 0 ? void 0 : e.preventDefault) === null || _a === void 0 ? void 0 : _a.call(e);
        (_b = e === null || e === void 0 ? void 0 : e.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(e);
        if (typeof props.clearedValue === 'function') {
            handleOnChange(props.clearedValue());
        }
        else {
            handleOnChange(props.clearedValue);
        }
        (_c = originalFieldRef.current) === null || _c === void 0 ? void 0 : _c.focus();
        if (typeof props.onClear === 'function') {
            props.onClear(e);
        }
    }, [props.readonly, props.disabled, handleOnChange, props.onClear, props.clearedValue]);
    useImperativeHandle(forwardedRef, () => ({
        originalFieldRef,
        focus() {
            var _a;
            (_a = originalFieldRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        },
        clear() {
            handleOnClear();
        },
    }), [handleOnClear]);
    return Object.assign(Object.assign({}, typedOmit(props, ['id', 'field'])), { 
        // errorMessage: props.errorMessage || props.field?.error,
        id,
        size,
        //
        originalFieldRef, value: innerValue, onChange: handleOnChange, 
        //
        isInvalid,
        isFocused,
        isUnfocused, isEmpty: isAbsoluteEmpty(innerValue), onClick: handleOnClick, onFocus: handleOnFocus, onBlur: handleOnBlur, onClear: props.clear || props.onClear ? handleOnClear : undefined });
}
